// === UK weather preview (Add / Edit Company) ===
// Shown under the address once a UK postcode has filled lat/lng, so the
// superadmin can see the company's weather working before saving. Same source
// as the customer dashboard banner: MET Norway via the API.

import { useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';

interface WeatherDay {
  readonly dateTime: string;
  readonly high: number;
  readonly low: number;
  readonly precis: string;
}

interface WeatherData {
  readonly locationName: string;
  readonly observational: {
    readonly temperature: number;
    readonly humidity: number;
    readonly windSpeed: number;
    readonly windDirection: string;
  } | null;
  readonly forecast: { readonly description: string; readonly high: number; readonly low: number } | null;
  readonly sevenDayForecast: readonly WeatherDay[] | null;
  readonly attribution: string;
}

interface Props {
  readonly apiUrl: string;
  readonly token: string;
  readonly lat: string;
  readonly lon: string;
  readonly name: string;
}

function Stat({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={700}>{value}</Typography>
    </Box>
  );
}

const dayLabel = (iso: string): string =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });

export function UkWeatherPreview({ apiUrl, token, lat, lon, name }: Props) {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ lat, lon, name }).toString();
    void fetch(`${apiUrl}/api/v1/admin/postcodes/uk/weather?${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
    })
      .then(async (res) => {
        const json = (await res.json()) as { data?: WeatherData; error?: string };
        if (!res.ok || !json.data) throw new Error(json.error ?? `Weather lookup failed (${res.status})`);
        setWeather(json.data);
      })
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) {
          setWeather(null);
          setError((e as Error).message);
        }
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => { ctrl.abort(); };
    // name is display-only; refetch only when the location changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token, lat, lon]);

  return (
    <Paper variant="outlined" sx={{ p: 2, width: '100%' }}>
      <Typography variant="caption" color="text.secondary">Weather preview — what this company will see</Typography>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={16} /> <Typography variant="body2">Loading weather…</Typography>
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
      ) : weather ? (
        <>
          <Stack direction="row" spacing={3} alignItems="baseline" sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={700}>{name || weather.locationName || 'This location'}</Typography>
            {weather.observational ? (
              <>
                <Typography variant="h5" fontWeight={700}>{weather.observational.temperature}°C</Typography>
                <Typography variant="body2">{weather.forecast?.description ?? '—'}</Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">No current conditions</Typography>
            )}
          </Stack>
          {/* Same stats as the customer dashboard banner. */}
          <Stack direction="row" spacing={3} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Stat label="Humidity" value={weather.observational ? `${weather.observational.humidity}%` : '—'} />
            <Stat label="Direction" value={weather.observational?.windDirection ?? '—'} />
            <Stat label="Wind" value={weather.observational ? `${weather.observational.windSpeed} km/h` : '—'} />
            <Stat label="High" value={weather.forecast ? `${weather.forecast.high}°` : '—'} />
            <Stat label="Low" value={weather.forecast ? `${weather.forecast.low}°` : '—'} />
          </Stack>
          {weather.sevenDayForecast ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={2} sx={{ overflowX: 'auto' }}>
                {weather.sevenDayForecast.map((d) => (
                  <Box key={d.dateTime} sx={{ minWidth: 72, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{dayLabel(d.dateTime)}</Typography>
                    <Typography variant="body2" fontWeight={700}>{d.high}° / {d.low}°</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>{d.precis}</Typography>
                  </Box>
                ))}
              </Stack>
            </>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'right' }}>
            {weather.attribution}
          </Typography>
        </>
      ) : null}
    </Paper>
  );
}
